import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { submitBusinessApplication } from "@/lib/api";

/**
 * BusinessApplicationForm — the intake form on the
 * "Start Your Own Vending Machine Business" landing page.
 *
 * On submit it POSTs to /api/business-applications, which persists to
 * MongoDB and sends a notification email to info.nector@gmail.com.
 * On success the user is redirected to /thank-you (same success
 * experience as the regular booking forms, which also fires the
 * Google Ads lead-form conversion).
 */
export default function BusinessApplicationForm({ testIdPrefix = "biz-app" }) {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    current_occupation: "",
    country_region: "Australia",
    preferred_city_state: "",
    investment_readiness: "",
    vending_experience: "",
    machines_interested: "5",
    desired_start_date: "",
    how_heard: "",
    questions: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await submitBusinessApplication({ ...form, source: "start-vending-business" });
      navigate("/thank-you");
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full rounded-lg bg-[#0f0f10] border border-white/10 px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#FE7200] focus:ring-2 focus:ring-[#FE7200]/30";

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-form`}
      className="rounded-2xl border border-white/10 bg-[#121212] p-6 sm:p-8 shadow-[0_30px_60px_-20px_rgba(254,114,0,0.25)]"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Full Name" required>
          <input
            required
            data-testid={`${testIdPrefix}-full-name-input`}
            value={form.full_name}
            onChange={update("full_name")}
            placeholder="e.g. Daniel Smith"
            className={inputBase}
          />
        </Field>
        <Field label="Email" required>
          <input
            type="email"
            required
            data-testid={`${testIdPrefix}-email-input`}
            value={form.email}
            onChange={update("email")}
            placeholder="you@company.com"
            className={inputBase}
          />
        </Field>
        <Field label="Phone Number" required>
          <input
            type="tel"
            required
            data-testid={`${testIdPrefix}-phone-input`}
            value={form.phone}
            onChange={update("phone")}
            placeholder="04xx xxx xxx"
            className={inputBase}
          />
        </Field>
        <Field label="Current Occupation">
          <input
            data-testid={`${testIdPrefix}-occupation-input`}
            value={form.current_occupation}
            onChange={update("current_occupation")}
            placeholder="e.g. Small business owner"
            className={inputBase}
          />
        </Field>
        <Field label="Country / Region">
          <select
            data-testid={`${testIdPrefix}-country-select`}
            value={form.country_region}
            onChange={update("country_region")}
            className={inputBase}
          >
            <option>Australia</option>
            <option>New Zealand</option>
            <option>United Kingdom</option>
            <option>United States</option>
            <option>Canada</option>
            <option>Other</option>
          </select>
        </Field>
        <Field label="Preferred City / State">
          <input
            data-testid={`${testIdPrefix}-city-input`}
            value={form.preferred_city_state}
            onChange={update("preferred_city_state")}
            placeholder="e.g. Sydney NSW"
            className={inputBase}
          />
        </Field>
        <Field label="Investment Readiness" required>
          <select
            required
            data-testid={`${testIdPrefix}-investment-select`}
            value={form.investment_readiness}
            onChange={update("investment_readiness")}
            className={inputBase}
          >
            <option value="">Select an option…</option>
            <option>Ready to invest $35,000 now</option>
            <option>Ready in the next 1–3 months</option>
            <option>Need financing / payment plan</option>
            <option>Just exploring for now</option>
          </select>
        </Field>
        <Field label="Vending Experience">
          <select
            data-testid={`${testIdPrefix}-experience-select`}
            value={form.vending_experience}
            onChange={update("vending_experience")}
            className={inputBase}
          >
            <option value="">Select an option…</option>
            <option>None — first-time operator</option>
            <option>Some — owned 1–2 machines</option>
            <option>Extensive — operated multiple machines</option>
          </select>
        </Field>
        <Field label="How Many Machines Interested In">
          <select
            data-testid={`${testIdPrefix}-machines-select`}
            value={form.machines_interested}
            onChange={update("machines_interested")}
            className={inputBase}
          >
            <option value="5">5 (turnkey package)</option>
            <option value="1-2">1–2 to start</option>
            <option value="10+">10+ (multi-site rollout)</option>
          </select>
        </Field>
        <Field label="Desired Start Date">
          <input
            type="date"
            data-testid={`${testIdPrefix}-start-date-input`}
            value={form.desired_start_date}
            onChange={update("desired_start_date")}
            className={inputBase}
          />
        </Field>
        <Field label="How Did You Hear About Us?" className="md:col-span-2">
          <input
            data-testid={`${testIdPrefix}-how-heard-input`}
            value={form.how_heard}
            onChange={update("how_heard")}
            placeholder="Google, LinkedIn, referral, ad…"
            className={inputBase}
          />
        </Field>
        <Field label="Questions or Notes" className="md:col-span-2">
          <textarea
            rows={5}
            data-testid={`${testIdPrefix}-questions-input`}
            value={form.questions}
            onChange={update("questions")}
            placeholder="Anything specific you'd like to know about site selection, the guarantee, training, or timing?"
            className={inputBase}
          />
        </Field>
      </div>

      {error && (
        <div
          data-testid={`${testIdPrefix}-error-message`}
          className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit-button`}
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#FE7200] px-6 py-4 font-display font-bold uppercase tracking-[0.18em] text-sm text-black hover:bg-[#FF8C2E] disabled:opacity-60 transition-colors"
      >
        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
        {submitting ? "Submitting…" : "Submit My Application"}
      </button>

      <p className="mt-4 text-xs text-zinc-500 text-center">
        By submitting, you agree to be contacted about the vending business package. No obligation.
      </p>
    </form>
  );
}

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-semibold tracking-wide text-zinc-300 mb-1.5">
        {label} {required && <span className="text-[#FE7200]">*</span>}
      </span>
      {children}
    </label>
  );
}
